import { sameWindow } from '../bookings/domain';
import type { DateWindow } from '../bookings/types';

// Is the week panel looking at data for the week it is showing?
//
// Three independent hooks feed it — shifts, bookings, leave — and all three
// HOLD their previous window's data across a change rather than blanking, which
// is what stops the panel flashing. The cost of that hold is that "the request
// finished" no longer means "the data is for this week": one hook can settle on
// the new window while another is still serving the old one, and a panel that
// renders the pair together is comparing last week's roster against this week's
// dates. It reported zero filled slots and said "Nobody is rostered this week"
// about a fully rostered week (PR #164, codex P2).
//
// So the question asked here is not whether anything is in flight. It is
// whether each set on hand is FOR the requested week — which every hook can
// answer exactly, by echoing the window its data belongs to. The predicate is
// pure and separate from the component so it can be tested without a fetch.
//
// This replaces a four-second timer. A timer cannot answer the question; it
// only decides how long to wait before asserting the answer anyway, which on a
// slow or failed request is the same wrong answer arriving later.

export interface WeekWindows {
  // The week the panel is displaying.
  requested: DateWindow;
  // The window each set on hand actually belongs to; null before a first load.
  shifts: DateWindow | null;
  bookings: DateWindow | null;
  leave: DateWindow | null;
  // Any of the three requests failed for this window.
  failed?: boolean;
}

export interface WeekLoadState {
  loading: boolean;
  failed: boolean;
}

export function weekLoadState({ requested, shifts, bookings, leave, failed }: WeekWindows): WeekLoadState {
  // FAILURE OUTRANKS WAITING. A failed window never catches up, so holding the
  // spinner would spin for ever; and letting it fall through to "settled" would
  // present a short roster or an undercounted dog load as the week's own quiet
  // truth. Neither is honest, so the panel is told to say so.
  if (failed) return { loading: false, failed: true };
  const settled =
    sameWindow(shifts, requested) && sameWindow(bookings, requested) && sameWindow(leave, requested);
  return { loading: !settled, failed: false };
}
